import { Dialog } from 'radix-ui';
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { clamp } from '../../core/math';

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  width?: number;
  disabled?: boolean;
}

/**
 * Keeps its own draft string so that typing "1" on the way to "150" does not get
 * clamped to the minimum on every keystroke.
 */
export function NumberInput({
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  width,
  disabled,
}: NumberInputProps) {
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);
  const [shown, setShown] = useState(value);

  // Adjust the draft while rendering rather than in an effect, so an external
  // change never shows a stale number for one frame.
  if (!editing && value !== shown) {
    setShown(value);
    setDraft(String(value));
  }

  const commit = (raw: string) => {
    const parsed = Number.parseFloat(raw.replace(',', '.'));
    if (Number.isFinite(parsed)) onChange(clamp(parsed, min, max));
    else setDraft(String(value));
  };

  return (
    <input
      className="number-input"
      style={width ? { width } : undefined}
      type="text"
      inputMode="decimal"
      disabled={disabled}
      value={draft}
      onFocus={() => setEditing(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={(event) => {
        setEditing(false);
        commit(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          commit(event.currentTarget.value);
          event.currentTarget.blur();
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          const direction = event.key === 'ArrowUp' ? 1 : -1;
          const multiplier = event.shiftKey ? 10 : 1;
          onChange(clamp(value + direction * step * multiplier, min, max));
        }
      }}
    />
  );
}

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  onCommitStart?: () => void;
  onCommitEnd?: () => void;
}

export function SliderRow({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  onCommitStart,
  onCommitEnd,
}: SliderRowProps) {
  return (
    <div className="slider-row">
      <span className="field__label">{label}</span>
      <input
        className="slider"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onPointerDown={onCommitStart}
        onPointerUp={onCommitEnd}
        onChange={(event) => onChange(Number.parseFloat(event.target.value))}
      />
      <NumberInput value={value} onChange={onChange} min={min} max={max} step={step} />
    </div>
  );
}

interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function Checkbox({ label, checked, onChange }: CheckboxProps) {
  return (
    <label className="checkbox">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

interface SelectProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  title?: string;
}

export function Select<T extends string>({ value, onChange, options, title }: SelectProps<T>) {
  return (
    <select
      className="select"
      title={title}
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * No dimming backdrop; clicks outside pass through so the canvas stays visible
   * and usable. Drag the title bar to reposition.
   */
  floating?: boolean;
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  className,
  floating = false,
}: ModalProps) {
  const contentClass = [
    'dialog',
    floating ? 'dialog--floating' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const initialOffset = floating ? { x: 180, y: -40 } : { x: 0, y: 0 };
  const [offset, setOffset] = useState(initialOffset);
  const [seenOpen, setSeenOpen] = useState(open);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) setOffset(initialOffset);
  }

  const onTitlePointerDown = (event: ReactPointerEvent<HTMLHeadingElement>) => {
    if (!floating || event.button !== 0) return;
    event.preventDefault();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onTitlePointerMove = (event: ReactPointerEvent<HTMLHeadingElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  };

  const onTitlePointerUp = (event: ReactPointerEvent<HTMLHeadingElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <Dialog.Root
      open={open}
      modal={!floating}
      onOpenChange={(next) => !next && onClose()}
    >
      <Dialog.Portal>
        {!floating && <Dialog.Overlay className="dialog-overlay" />}
        <Dialog.Content
          className={contentClass}
          style={
            floating
              ? { transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))` }
              : undefined
          }
          onPointerDownOutside={floating ? (event) => event.preventDefault() : undefined}
          onInteractOutside={floating ? (event) => event.preventDefault() : undefined}
        >
          <Dialog.Title
            className={floating ? 'dialog__title dialog__title--drag' : 'dialog__title'}
            onPointerDown={onTitlePointerDown}
            onPointerMove={onTitlePointerMove}
            onPointerUp={onTitlePointerUp}
            onPointerCancel={onTitlePointerUp}
          >
            {title}
          </Dialog.Title>
          <div className="dialog__body">{children}</div>
          {footer && <div className="dialog__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Icon({ path, size = 18 }: { path: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
